'use client';

import { useMemo, useCallback } from 'react';
import { MEDIA_CACHE_VERSION } from '@/config/media-config';
import { getCanonicalMediaUrl, normalizeMediaUrl } from '@/lib/url-normalization';

// Cache version - should match the version in the API route
// Increment this when media handlers change to force cache refresh
const CACHE_VERSION = MEDIA_CACHE_VERSION;

function buildVersionedMediaApiUrl(s3Key: string, revision?: string | null): string {
  const params = new URLSearchParams({
    key: s3Key,
    view: 'true',
    v: CACHE_VERSION,
  });

  if (revision) {
    params.set('r', revision);
  }

  return `/api/media/download?${params.toString()}`;
}

// Global cache for S3 URLs to avoid multiple API calls
class S3FileCache {
  private cache = new Map<string, string>();
  private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutos
  private timestamps = new Map<string, number>();
  private pendingRequests = new Map<string, Promise<string>>();
  private currentVersion = CACHE_VERSION;

  get(s3Key: string): string | null {
    const cached = this.cache.get(s3Key);
    const timestamp = this.timestamps.get(s3Key);
    
    // Check cache validity (no longer checking version param - using canonical URLs)
    if (cached && timestamp && (Date.now() - timestamp) < this.CACHE_DURATION) {
      // Normalize to canonical form in case cache has old format
      return getCanonicalMediaUrl(cached) || cached;
    }
    
    // Clean expired entry
    if (cached) {
      this.cache.delete(s3Key);
      this.timestamps.delete(s3Key);
    }
    
    return null;
  }

  set(s3Key: string, url: string): void {
    this.cache.set(s3Key, url);
    this.timestamps.set(s3Key, Date.now());
    
    // Limit cache size
    if (this.cache.size > 200) {
      const oldestKey = Array.from(this.timestamps.entries())
        .sort(([, a], [, b]) => a - b)[0][0];
      this.cache.delete(oldestKey);
      this.timestamps.delete(oldestKey);
    }
  }

  // Prevent duplicate requests for the same S3 key
  async getOrFetch(s3Key: string): Promise<string> {
    // Check cache first
    const cached = this.get(s3Key);
    if (cached) {
      return cached;
    }

    // Check if there's already a pending request for this key
    const pending = this.pendingRequests.get(s3Key);
    if (pending) {
      return pending;
    }

    // Create new request with explicit cache version to avoid stale browser assets.
    const apiUrl = buildVersionedMediaApiUrl(s3Key);
    const promise = Promise.resolve(apiUrl);
    
    this.pendingRequests.set(s3Key, promise);
    
    try {
      const url = await promise;
      this.set(s3Key, url);
      return url;
    } finally {
      this.pendingRequests.delete(s3Key);
    }
  }

  clear(): void {
    this.cache.clear();
    this.timestamps.clear();
    this.pendingRequests.clear();
  }

  // Clear entries with old cache versions
  clearOldVersions(): void {
    const entriesToDelete: string[] = [];
    
    for (const [key, url] of this.cache.entries()) {
      // Check if URL has version parameter that doesn't match current version
      const urlMatch = url.match(/[?&]v=([^&]+)/);
      if (urlMatch && urlMatch[1] !== this.currentVersion) {
        entriesToDelete.push(key);
      }
    }
    
    entriesToDelete.forEach(key => {
      this.cache.delete(key);
      this.timestamps.delete(key);
    });
    
    if (entriesToDelete.length > 0) {
      console.log(`[S3FileCache] Cleared ${entriesToDelete.length} entries with old cache versions`);
    }
  }

  // Update cache version (call this when handlers change)
  updateVersion(newVersion: string): void {
    if (newVersion !== this.currentVersion) {
      this.currentVersion = newVersion;
      this.clearOldVersions();
    }
  }

  getStats(): {
    cacheSize: number;
    pendingRequests: number;
    oldestEntry: number | null;
    newestEntry: number | null;
  } {
    const timestamps = Array.from(this.timestamps.values());
    
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      oldestEntry: timestamps.length > 0 ? Math.min(...timestamps) : null,
      newestEntry: timestamps.length > 0 ? Math.max(...timestamps) : null
    };
  }
}

// Global instance
const s3FileCache = new S3FileCache();

// Session-based tracking for browser-cached media
const browserCachedMedia = new Set<string>();

/**
 * Check if media is already in browser cache
 */
async function isMediaCached(url: string): Promise<boolean> {
  if (typeof window === 'undefined') {
    return false;
  }

  // Check session cache first
  if (browserCachedMedia.has(url)) {
    return true;
  }

  return new Promise((resolve) => {
    // For images
    if (url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) || url.includes('image')) {
      const img = new Image();
      img.onload = () => {
        browserCachedMedia.add(url);
        resolve(true);
      };
      img.onerror = () => resolve(false);
      img.src = url;
      
      // Timeout after 50ms - if cached, it should load almost instantly
      setTimeout(() => {
        if (img.complete && img.naturalWidth > 0) {
          browserCachedMedia.add(url);
          resolve(true);
        } else {
          resolve(false);
        }
      }, 50);
    }
    // For videos
    else if (url.match(/\.(mp4|webm|mov)$/i) || url.includes('video')) {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.onloadedmetadata = () => {
        browserCachedMedia.add(url);
        resolve(true);
      };
      video.onerror = () => resolve(false);
      video.src = url;
      
      // Timeout after 100ms for videos
      setTimeout(() => {
        if (video.readyState >= 1) {
          browserCachedMedia.add(url);
          resolve(true);
        } else {
          resolve(false);
        }
      }, 100);
    } else {
      // For other types, assume not cached
      resolve(false);
    }
  });
}

interface UseS3FileCacheOptions {
  enableCache?: boolean;
  cacheKey?: string;
}

export function useS3FileCache(src: string, options: UseS3FileCacheOptions = {}) {
  const { enableCache = true, cacheKey } = options;

  // Analyze the source URL
  const fileAnalysis = useMemo(() => {
    if (!src) return null;

    // Check if it's already an API URL with key parameter
    const isApiUrl = src.includes('/api/media/download') || src.includes('/api/v1/media/download');
    if (isApiUrl) {
      try {
        // Handle both absolute and relative URLs
        const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
        const url = new URL(src, baseUrl);
        const keyParam = url.searchParams.get('key');
        const viewParam = url.searchParams.get('view');
        const versionParam = url.searchParams.get('v');
        const revisionParam = url.searchParams.get('r');
        
        if (keyParam) {
          // Use canonical URL (without version param) for deduplication
          const canonicalUrl = getCanonicalMediaUrl(src) || src;
          
          return {
            isS3Url: true,
            s3Key: keyParam,
            originalSrc: canonicalUrl, // Return canonical URL for deduplication
            isAlreadyApiUrl: true,
            versionParam,
            revisionParam,
          };
        }
      } catch (_error) {
        // If URL parsing fails, continue with normal detection
      }
    }

    // Check if it's a direct S3 key (not a URL, just a filename/key)
    // This handles cases where the database stores just the S3 key
    if (!src.includes('://') && !src.startsWith('/') && !src.startsWith('http')) {
      // Likely a direct S3 key
      return {
        isS3Url: true,
        s3Key: src,
        originalSrc: src,
        isDirectKey: true
      };
    }

    // Detectar si es una URL de S3
    const isS3Url = src.includes('s3.amazonaws.com') ||
                    src.includes('e-voque') ||
                    (process.env.NEXT_PUBLIC_S3_URL_PREFIX && src.startsWith(process.env.NEXT_PUBLIC_S3_URL_PREFIX));
    
    let s3Key: string | null = null;
    
    
    if (isS3Url) {
      try {
        // Intentar extraer la clave de S3 de la URL
        const url = new URL(src);
        const pathParts = url.pathname.split('/');
        
        // Eliminar la primera parte vacía del pathname que comienza con /
        pathParts.shift();
        
        // La clave es el resto del path
        s3Key = pathParts.join('/');
      } catch (_error) {
        s3Key = null;
      }
    }

    const result = {
      isS3Url,
      s3Key,
      originalSrc: src,
      isAlreadyApiUrl: false,
      isDirectKey: false
    };
    
    return result;
  }, [src]);

  // Get the optimized URL
  const getOptimizedUrl = useCallback(async (): Promise<string> => {
    if (!fileAnalysis) return src;

    const { isS3Url, s3Key, isAlreadyApiUrl, revisionParam } = fileAnalysis as any;

    // If it's already an API URL, return as-is
    if (isAlreadyApiUrl) {
      const normalizedUrl = normalizeMediaUrl(src) || src;
      const isCached = await isMediaCached(normalizedUrl);
      if (isCached) {
        return normalizedUrl;
      }
      return normalizedUrl;
    }

    // ALWAYS redirect S3 URLs through our API
    if (isS3Url && s3Key) {
      // Use cache key if provided, otherwise use s3Key
      const key = cacheKey || s3Key;
      const apiUrl = revisionParam
        ? buildVersionedMediaApiUrl(key, revisionParam)
        : await s3FileCache.getOrFetch(key);
      
      // Check browser cache - if cached, we can use it immediately
      const isCached = await isMediaCached(apiUrl);
      if (isCached) {
        return apiUrl;
      }
      
      return apiUrl;
    }

    // For non-S3 URLs, return as-is
    return src;
  }, [fileAnalysis, src, cacheKey]);

  // Get cached URL synchronously (for immediate use)
  const getCachedUrl = useCallback((): string | null => {
    if (!fileAnalysis || !enableCache) return null;

    const { isS3Url, s3Key } = fileAnalysis;

    if (isS3Url && s3Key) {
      const key = cacheKey || s3Key;
      return s3FileCache.get(key);
    }

    return null;
  }, [fileAnalysis, enableCache, cacheKey]);

  // Get the final URL (cached or generate API URL)
  // CRITICAL: Use canonical URLs (without version params) to prevent duplicate downloads
  const finalUrl = useMemo(() => {
    if (!fileAnalysis) return src;

    const { isS3Url, s3Key, isAlreadyApiUrl, revisionParam } = fileAnalysis as any;

    // If it's already an API URL, normalize to canonical form for deduplication
    if (isAlreadyApiUrl) {
      return normalizeMediaUrl(src) || src;
    }

    // ALWAYS redirect S3 URLs through our API, never load directly from S3
    if (isS3Url && s3Key) {
      const key = cacheKey || s3Key;
      const cached = s3FileCache.get(key);
      
      if (cached) {
        // Normalize cached URL to canonical form
        const canonicalCached = getCanonicalMediaUrl(cached) || cached;
        
        // Check browser cache asynchronously (don't block render)
        if (typeof window !== 'undefined') {
          isMediaCached(canonicalCached).catch(() => {
            // Silently handle cache check errors
          });
        }
        return canonicalCached;
      }
      
      const apiUrl = buildVersionedMediaApiUrl(s3Key, revisionParam);
      s3FileCache.set(key, apiUrl);
      
      // Check browser cache asynchronously (don't block render)
      if (typeof window !== 'undefined') {
        isMediaCached(apiUrl).catch(() => {
          // Silently handle cache check errors
        });
      }
      
      return apiUrl;
    }

    // For non-S3 URLs, normalize to canonical form if it's a media URL
    return getCanonicalMediaUrl(src) || src;
  }, [fileAnalysis, src, cacheKey]);

  // Clear cache function
  const clearCache = useCallback(() => {
    s3FileCache.clear();
  }, []);

  // Get cache stats
  const getCacheStats = useCallback(() => {
    return s3FileCache.getStats();
  }, []);

  return {
    finalUrl,
    isS3Url: fileAnalysis?.isS3Url || false,
    s3Key: fileAnalysis?.s3Key || null,
    getOptimizedUrl,
    getCachedUrl,
    clearCache,
    getCacheStats
  };
}

// Export the cache instance for direct access if needed
export { s3FileCache }; 